import React, { Component, type ReactNode } from "react";
import { Box, Button, Typography } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import { captureException } from "../../config/sentry";

interface RootErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

interface RootErrorBoundaryProps {
  children: ReactNode;
}

/**
 * RootErrorBoundary — Last-resort error boundary above AuthProvider.
 *
 * Sits directly under ThemeProvider / CssBaseline and catches errors that
 * occur *before* auth context is available (e.g. AuthProvider itself
 * crashing). Because there is no auth, router, or React Query context at
 * this level, the only recovery action is a full page reload.
 *
 * For richer error recovery (retry, navigate home) see AppErrorBoundary,
 * which lives inside AuthProvider / TenantProvider.
 */
export class RootErrorBoundary extends Component<
  RootErrorBoundaryProps,
  RootErrorBoundaryState
> {
  constructor(props: RootErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): RootErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("[RootErrorBoundary] Uncaught error:", error, errorInfo);

    captureException(error, {
      componentStack: errorInfo.componentStack,
      boundary: "RootErrorBoundary",
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const error = this.state.error;

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          p: 4,
          textAlign: "center",
          backgroundColor: "background.default",
        }}
      >
        <ErrorOutlineIcon sx={{ fontSize: 64, color: "error.main", mb: 2 }} />

        <Typography variant="h5" gutterBottom fontWeight={600}>
          Something went wrong
        </Typography>

        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mb: 3, maxWidth: 500 }}
        >
          The application encountered an unexpected error. Please reload the
          page to try again.
        </Typography>

        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={this.handleReload}
          size="large"
        >
          Reload Page
        </Button>

        {import.meta.env.DEV && error && (
          <Box
            sx={{
              mt: 4,
              p: 2,
              backgroundColor: "grey.100",
              borderRadius: 1,
              maxWidth: 800,
              width: "100%",
              textAlign: "left",
            }}
          >
            <Typography variant="caption" fontWeight={600} gutterBottom>
              Developer Info:
            </Typography>
            <Typography
              variant="caption"
              component="pre"
              sx={{
                whiteSpace: "pre-wrap",
                fontSize: "0.7rem",
                wordBreak: "break-word",
              }}
            >
              {error.stack}
            </Typography>
          </Box>
        )}
      </Box>
    );
  }
}
